import React, { useState, useEffect, useRef } from 'react';
import { Compass, Tag } from 'lucide-react';
import { apiClient } from '../../api/client';
import {
  CANONICAL_SPOT_SYMBOLS,
  CANONICAL_PERP_SYMBOLS,
  CANONICAL_TIMEFRAMES,
} from '../../shared/constants/watchlist';
import {
  QUANT_FACT_TAG_DICTIONARY_V1,
  VENUE_INFO,
  getTagPriority,
} from './constants';

interface CellItem {
  status?: string;
  directional_state?: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  active_tag_codes?: string[];
  active_tag_ids?: number[];
  as_of_candle_close_utc?: string;
  data_freshness_seconds?: number;
}

interface AssetBadgeItem {
  tag_id?: number;
  tag_code: string;
  name: string;
  category?: string;
  scope?: string;
  display_variant?: string;
}

interface MatrixResponse {
  as_of_utc?: string;
  market_type?: 'SPOT' | 'PERP';
  schema_version?: string;
  dictionary_version?: string;
  dictionary_digest?: string;
  dictionary_v2_digest?: string;
  symbols?: string[];
  timeframes?: string[];
  assets?: Array<{
    asset_id: string;
    symbol: string;
    asset_badges: AssetBadgeItem[];
  }>;
  asset_badges?: Record<string, AssetBadgeItem[]>;
  matrix?: Record<string, Record<string, CellItem>>;
}

import { VisibilityRequestCoordinator } from './TrendWeatherGrid';

export const RegimeMatrixView: React.FC = () => {
  const [marketType, setMarketType] = useState<'SPOT' | 'PERP'>('SPOT');
  const [matrixData, setMatrixData] = useState<MatrixResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDisconnected, setIsDisconnected] = useState<boolean>(false);
  const [selectedCell, setSelectedCell] = useState<{ symbol: string; timeframe: string; cell: CellItem } | null>(null);
  const matrixDataRef = React.useRef<MatrixResponse | null>(null);

  const coordinatorRef = useRef<VisibilityRequestCoordinator<MatrixResponse>>(new VisibilityRequestCoordinator());

  // Stage 066/070: 60-second cadence + Page Visibility pause/resume + in-flight request coalescing coordinator
  useEffect(() => {
    let active = true;
    let timer: NodeJS.Timeout | null = null;

    const fetchMatrix = async () => {
      try {
        coordinatorRef.current.cancelInFlight();
        const res = await coordinatorRef.current.execute(
          (signal) =>
            apiClient.get<MatrixResponse>('/api/v1/market/tags/matrix', {
              params: { market_type: marketType },
              signal,
            }),
          true
        );
        if (active && res) {
          matrixDataRef.current = res;
          setMatrixData(res);
          setLoading(false);
          setIsDisconnected(false);
        }
      } catch {
        if (active && !document.hidden) {
          if (!matrixDataRef.current) setIsDisconnected(true);
          setLoading(false);
        }
      }
    };

    const startTimer = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        if (!document.hidden && active) {
          fetchMatrix();
        }
      }, 60000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        coordinatorRef.current.onHide();
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      } else {
        if (active) {
          coordinatorRef.current.onResume();
          fetchMatrix();
          startTimer();
        }
      }
    };

    fetchMatrix();
    if (!document.hidden) {
      startTimer();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      active = false;
      coordinatorRef.current.cancelInFlight();
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [marketType]);

  const currentSymbols = marketType === 'SPOT' ? CANONICAL_SPOT_SYMBOLS : CANONICAL_PERP_SYMBOLS;
  const timeframes = matrixData?.timeframes || CANONICAL_TIMEFRAMES;
  const digest = matrixData?.dictionary_v2_digest || matrixData?.dictionary_digest || (loading ? 'Loading...' : '--');

  const renderCellContent = (sym: string, tf: string) => {
    const liveItem = matrixData?.matrix?.[sym]?.[tf];

    const status = liveItem?.status;
    const rawCodes = liveItem?.active_tag_codes || [];
    // Strict isolation: Sector tags are decoupled from MTF cell matrix
    const codes = rawCodes.filter((c) => !c.startsWith('SECTOR_'));
    const directionalState = liveItem?.directional_state || 'NEUTRAL';

    const item: CellItem = {
      status,
      directional_state: directionalState,
      active_tag_codes: codes,
      as_of_candle_close_utc: liveItem?.as_of_candle_close_utc || '--',
      data_freshness_seconds: liveItem?.data_freshness_seconds,
    };

    if (status === 'READY_POSITIVE' && codes.length > 0) {
      // Sort by canonical priority
      const sortedCodes = codes.slice().sort((a, b) => getTagPriority(a) - getTagPriority(b));
      const visibleCodes = sortedCodes.slice(0, 2);
      const overflowCount = sortedCodes.length - visibleCodes.length;

      let badgeClass = 'badge-ready-positive';
      let badgeIcon = '🟢';
      if (directionalState === 'BEARISH') {
        badgeClass = 'badge-bearish';
        badgeIcon = '🔴';
      } else if (directionalState === 'NEUTRAL') {
        badgeClass = 'badge-ready-negative';
        badgeIcon = '⚪';
      }

      return (
        <div
          tabIndex={0}
          role="button"
          style={{
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            minHeight: '44px',
            padding: '4px 6px',
            borderRadius: '6px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            boxShadow: 'var(--shadow-sm)',
            transition: 'all 0.15s ease',
          }}
          onClick={() => setSelectedCell({ symbol: sym, timeframe: tf, cell: item })}
          title={`标的: ${sym}\n周期: ${tf}\n状态: ${directionalState}\n激活标签: ${sortedCodes.map((c) => QUANT_FACT_TAG_DICTIONARY_V1[c]?.name || c).join(', ')}`}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className={`tag-cell-badge ${badgeClass}`}>
              {badgeIcon} {codes.length} 标签
            </span>
          </div>
          <div className="tag-chips-wrapper">
            {visibleCodes.map((code) => {
              const meta = QUANT_FACT_TAG_DICTIONARY_V1[code];
              const name = meta?.name || code;
              const cls = meta?.cls || 'tag-chip-neutral';
              return (
                <span
                  key={code}
                  className={`tag-chip ${cls}`}
                  title={`${code} · ${meta?.category || ''}`}
                >
                  {name}
                </span>
              );
            })}
            {overflowCount > 0 && (
              <span
                className="tag-chip-more"
                title={`点击查看全部 ${codes.length} 个量化特征`}
              >
                +{overflowCount}
              </span>
            )}
          </div>
        </div>
      );
    }

    if (status === 'READY_NEUTRAL' || (status === 'READY_POSITIVE' && codes.length === 0) || status === 'READY_NEGATIVE' || codes.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '44px',
            borderRadius: '6px',
            background: 'var(--bg-card)',
            border: '1px dashed var(--border-subtle)',
          }}
          title="平稳基线 (无特征激活)"
        >
          <span className="tag-cell-badge badge-ready-negative">
            ⚪ 平稳
          </span>
        </div>
      );
    }

    if (status === 'INSUFFICIENT_HISTORY') {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '44px',
            borderRadius: '6px',
            background: 'var(--bg-card)',
            border: '1px dashed var(--border-subtle)',
          }}
          title="样本预热中"
        >
          <span className="tag-cell-badge badge-insufficient">
            🟡 预热中
          </span>
        </div>
      );
    }

    if (isDisconnected) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '44px',
            borderRadius: '6px',
            background: 'var(--bg-card)',
            border: '1px dashed var(--border-subtle)',
          }}
          title="通道离线"
        >
          <span className="tag-cell-badge badge-error">
            🔴 离线
          </span>
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '44px',
          borderRadius: '6px',
          background: 'var(--bg-card)',
          border: '1px dashed var(--border-subtle)',
        }}
        title="数据源暂未接入"
      >
        <span className="tag-cell-badge badge-unsupported">
          --
        </span>
      </div>
    );
  };

  return (
    <div className="view-panel active" id="view-regime-matrix" role="tabpanel">
      {/* Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Compass size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">态势感知与量化特征中枢</h2>
          </div>
          <span className="bilingual-header-subtitle-en">REGIME SEMANTICS & COPILOT</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            7 标的 × 7 周期多时间尺度量化特征矩阵 · 严格闭合烛线 (t) · 滞后 W ≥ 3s · 规范因果标签与稳态趋势引擎
          </p>
        </div>
      </div>

      {/* Multi-Timeframe Fact Tag Matrix Panel */}
      <section className="tags-matrix-panel">
        <div className="panel-title-bar">
          <div className="panel-title bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Tag size={14} style={{ color: 'var(--accent-cyan)' }} />
              <span className="bilingual-card-title-zh" style={{ fontSize: '14px' }}>
                行情标签多周期监控矩阵
              </span>
              <span className="venue-pill venue-hyperliquid" style={{ fontSize: '10px' }}>
                40 标签稳态趋势与波动率因果状态机引擎
              </span>
              {isDisconnected && (
                <span className="badge badge-bearish" style={{ fontSize: '10px' }}>
                  离线
                </span>
              )}
            </div>
            <span className="bilingual-card-subtitle-en">QUANT FACT TAGS MTF MATRIX</span>
          </div>

          <div className="tags-ctrl-bar">
            <div className="market-type-toggle">
              <button
                className={`toggle-btn ${marketType === 'SPOT' ? 'active' : ''}`}
                id="btn-toggle-spot"
                onClick={() => setMarketType('SPOT')}
              >
                现货主场 (SPOT)
              </button>
              <button
                className={`toggle-btn ${marketType === 'PERP' ? 'active' : ''}`}
                id="btn-toggle-perp"
                onClick={() => setMarketType('PERP')}
              >
                永续主场 (PERP)
              </button>
            </div>

            <div className="tags-legend" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className="legend-item">
                <span className="tag-cell-badge badge-ready-positive">🟢 特征激活</span>
              </span>
              <span className="legend-item">
                <span className="tag-cell-badge badge-ready-negative">⚪ 平稳/中性</span>
              </span>
              <span className="legend-item">
                <span className="tag-cell-badge badge-insufficient">🟡 样本预热中</span>
              </span>
              <span className="legend-item">
                <span className="tag-cell-badge badge-unsupported">⚪ 未接入</span>
              </span>
              <span className="legend-item">
                <span className="tag-cell-badge badge-error">🔴 提取异常/离线</span>
              </span>
            </div>
          </div>
        </div>

        {/* Digest Line */}
        <div className="digest-bar">
          <div>
            <span>规范字典摘要 (Digest): </span>
            <code className="digest-code" id="tags-dict-digest">{digest}</code>
          </div>
          <div>
            因果闭合判定: <strong style={{ color: 'var(--color-bullish)' }}>严格闭合烛线 (t) · 滞后 W ≥ 3s</strong>
          </div>
        </div>

        {/* Matrix Table */}
        <div style={{ overflowX: 'auto', position: 'relative' }}>
          <table className="matrix-table" id="tags-matrix-table">
            <thead>
              <tr>
                <th style={{ width: '95px' }}>标的资产</th>
                {timeframes.map((tf) => {
                  const lowerTf = tf.toLowerCase();
                  const isTactical = ['1m', '5m', '15m'].includes(lowerTf);
                  const isPrimary = ['30m', '1h'].includes(lowerTf);
                  const tierLabel = isTactical ? '战术' : isPrimary ? '主航道' : '战略';
                  const tierColor = isTactical ? 'var(--accent-cyan)' : isPrimary ? 'var(--accent-purple, var(--accent-primary))' : 'var(--color-warning)';
                  const isBoundary = lowerTf === '15m' || lowerTf === '1h';

                  return (
                    <th
                      key={tf}
                      style={{
                        borderRight: isBoundary ? '2px solid var(--border-card)' : undefined,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <span>{tf.toUpperCase()} 周期</span>
                        <span
                          style={{
                            fontSize: '8.5px',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            background: isTactical
                              ? 'var(--badge-info-bg)'
                              : isPrimary
                              ? 'var(--badge-purple-bg, var(--badge-info-bg))'
                              : 'var(--badge-warning-bg)',
                            color: tierColor,
                            fontWeight: 600,
                          }}
                        >
                          {tierLabel}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody id="tags-matrix-tbody">
              {currentSymbols.map((sym) => {
                const vInfo = VENUE_INFO[sym] || {
                  venue: 'BINANCE',
                  className: 'venue-binance',
                  role: '主流资产',
                };
                return (
                  <tr key={sym}>
                    <td style={{ verticalAlign: 'top', padding: '6px 8px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)' }}>
                          {sym}
                        </span>
                        <span
                          style={{ fontSize: '9px', color: 'var(--text-muted)' }}
                          title={`流动性主市场: ${vInfo.venue}`}
                        >
                          {vInfo.role}
                        </span>
                      </div>
                    </td>
                    {timeframes.map((tf) => {
                      const lowerTf = tf.toLowerCase();
                      const isBoundary = lowerTf === '15m' || lowerTf === '1h';
                      return (
                        <td
                          key={tf}
                          style={{
                            verticalAlign: 'top',
                            padding: '4px 6px',
                            borderRight: isBoundary ? '2px solid var(--border-card)' : undefined,
                          }}
                        >
                          {renderCellContent(sym, tf)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Fact Tag Detail Modal */}
      {selectedCell && (
        <div
          className="modal-backdrop"
          onClick={() => setSelectedCell(null)}
        >
          <div
            className="glass-panel"
            style={{
              width: '500px',
              maxWidth: '92%',
              padding: '24px',
              borderRadius: '12px',
              border: '1px solid var(--border-glass)',
              background: 'var(--bg-surface-elevated)',
              boxShadow: 'var(--shadow-drawer)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {selectedCell.symbol} · {selectedCell.timeframe.toUpperCase()} 周期量化特征明细
              </h3>
              <button
                onClick={() => setSelectedCell(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '6px' }}>
                <span style={{ color: 'var(--text-muted)' }}>闭合烛线时间 (UTC):</span>
                <span className="mono">{selectedCell.cell.as_of_candle_close_utc || '--'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '6px' }}>
                <span style={{ color: 'var(--text-muted)' }}>数据新鲜度:</span>
                <span className="mono">{selectedCell.cell.data_freshness_seconds !== undefined ? `${selectedCell.cell.data_freshness_seconds}s 前` : '--'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '6px' }}>
                <span style={{ color: 'var(--text-muted)' }}>因果判定状态:</span>
                <span className={selectedCell.cell.status === 'READY_POSITIVE' ? 'tag-cell-badge badge-ready-positive' : 'tag-cell-badge badge-unsupported'}>
                  {selectedCell.cell.status === 'READY_POSITIVE'
                    ? `🟢 命中 ${selectedCell.cell.active_tag_codes?.length || 0} 个特征 (${selectedCell.cell.directional_state || 'READY'})`
                    : selectedCell.cell.status === 'READY_NEUTRAL'
                    ? '⚪ 平稳中性'
                    : selectedCell.cell.status || '--'}
                </span>
              </div>

              <div style={{ marginTop: '12px' }}>
                <span style={{ color: 'var(--text-muted)', display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  激活的规范特征 ({selectedCell.cell.active_tag_codes?.length || 0}):
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {(selectedCell.cell.active_tag_codes || []).map((code) => {
                    const meta = QUANT_FACT_TAG_DICTIONARY_V1[code];
                    const name = meta?.name || code;
                    const cls = meta?.cls || 'tag-chip-neutral';
                    return (
                      <span
                        key={code}
                        className={`tag-chip ${cls}`}
                        style={{ padding: '3px 8px', fontSize: '11px', borderRadius: '4px' }}
                        title={`${code} · 分类: ${meta?.category || ''}`}
                      >
                        {name} ({code})
                      </span>
                    );
                  })}
                  {(!selectedCell.cell.active_tag_codes || selectedCell.cell.active_tag_codes.length === 0) && (
                    <span style={{ color: 'var(--text-muted)' }}>暂无特征激活 (平稳中性基线)</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

